import { Component, ErrorInfo, ReactNode } from "react"
import styled from "styled-components"
import colors from "~constants/colors"

interface Props {
  children: ReactNode
  // When this changes after an error (e.g. the route path), the boundary clears
  // its error state so navigating away recovers without a full reload.
  resetKey?: string
}

interface State {
  hasError: boolean
}

// Isolates page render errors: a crash in the routed content shows a fallback in
// the content area instead of unmounting the whole app shell, so the header /
// nav / logout stay usable. Reset by navigating to another route.
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Page render error:", error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <Fallback>
          <Title>Something went wrong on this page.</Title>
          <Message>
            The rest of the app is still available — use the menu to navigate,
            or reload to try again.
          </Message>
          <ReloadButton onClick={() => window.location.reload()}>
            Reload
          </ReloadButton>
        </Fallback>
      )
    }
    return this.props.children
  }
}

const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  min-height: 300px;
  padding: 40px;
  text-align: center;
`

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.gray3};
  margin: 0;
`

const Message = styled.p`
  font-size: 14px;
  color: ${colors.gray6};
  max-width: 420px;
  margin: 0;
`

const ReloadButton = styled.button`
  margin-top: 8px;
  padding: 8px 18px;
  border: 1px solid ${colors.blue1};
  border-radius: 8px;
  background: ${colors.blue1};
  color: ${colors.white};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`

export default ErrorBoundary
